import { fetchClient } from "@/api/api";
import type {
	BatchDeleteTransferPlayersInput,
	BatchDeleteTransferPlayersOutput,
	TransferPlayerState,
	createActivePlayerInput,
	createActivePlayerOutput,
	deleteActivePlayerInput,
	deleteActivePlayerOutput,
	getActivePlayerInput,
	getActivePlayerOutput,
	getActivePlayerSuggestionsFacetsInput,
	getActivePlayerSuggestionsFacetsOutput,
	getActivePlayerSuggestionsInput,
	getActivePlayerSuggestionsOutput,
	getActivePlayersFacetsInput,
	getActivePlayersFacetsOutput,
	getAllActivePlayersInput,
	getAllActivePlayersOutput,
	getPlayerDatabaseFacetsOutput,
	getPlayerDatabaseInput,
	getPlayerDatabaseOutput,
	postAdminAllActivePlayersInput,
	postAdminAllActivePlayersOutput,
	refreshPlayerPricesInput,
	refreshPlayerPricesOutput,
	updateActivePlayerInput,
	updateActivePlayerOutput,
} from "@/api/custom_types";
import { createFacetSource } from "@/services/facets";

import useApiError from "@/hooks/useApiError";
import { useTranslation } from "@/hooks/useTranslation";
import type { ApiError } from "@/utils/fetching";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { invalidateQueries } from "@/utils/react-query";
import { toast } from "@st-marketplace/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ACTIVE_PLAYERS_SERVICE_KEYS } from "./keys";

/**
 * Active players and transfer-player suggestions share the same underlying
 * pool: adding/removing an active player changes which players are suggested.
 * Keep the suggestions list and its facets fresh whenever membership changes.
 */
const invalidateActivePlayerSuggestions = () => {
	invalidateQueries(
		ACTIVE_PLAYERS_SERVICE_KEYS.useGetActivePlayerSuggestions(),
	);
	invalidateQueries(
		ACTIVE_PLAYERS_SERVICE_KEYS.useGetActivePlayerSuggestionsFacets(),
	);
	invalidateQueries(ACTIVE_PLAYERS_SERVICE_KEYS.useGetPlayerDatabase());
	invalidateQueries(ACTIVE_PLAYERS_SERVICE_KEYS.useGetPlayerDatabaseFacets());
};

export const useGetAllActivePlayers: CustomQueryHook<
	getAllActivePlayersOutput,
	getAllActivePlayersInput
> = (params, options) => {
	return useQuery<getAllActivePlayersOutput, ApiError>({
		...options,
		queryKey: ACTIVE_PLAYERS_SERVICE_KEYS.useGetAllActivePlayers(params ?? {}),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/pa-transfer-player/",
				{
					params: { query: params ?? {} },
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
	});
};

export const useGetActivePlayersFacets: CustomQueryHook<
	getActivePlayersFacetsOutput,
	getActivePlayersFacetsInput
> = (params, options) => {
	return useQuery<getActivePlayersFacetsOutput, ApiError>({
		...options,
		queryKey: ACTIVE_PLAYERS_SERVICE_KEYS.useGetActivePlayersFacets(
			params ?? {},
		),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/pa-transfer-player/facets/",
				{
					params: { query: params ?? {} },
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
	});
};

export const useGetActivePlayerSuggestions: CustomQueryHook<
	getActivePlayerSuggestionsOutput,
	getActivePlayerSuggestionsInput
> = (params, options) => {
	return useQuery<getActivePlayerSuggestionsOutput, ApiError>({
		...options,
		queryKey: ACTIVE_PLAYERS_SERVICE_KEYS.useGetActivePlayerSuggestions(
			params ?? {},
		),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/pa-transfer-player-suggestions/",
				{
					params: { query: params ?? {} },
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
	});
};

export const useGetActivePlayerSuggestionsFacets: CustomQueryHook<
	getActivePlayerSuggestionsFacetsOutput,
	getActivePlayerSuggestionsFacetsInput
> = (params, options) => {
	return useQuery<getActivePlayerSuggestionsFacetsOutput, ApiError>({
		...options,
		queryKey: ACTIVE_PLAYERS_SERVICE_KEYS.useGetActivePlayerSuggestionsFacets(
			params ?? {},
		),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/pa-transfer-player-suggestions/facets/",
				{
					params: { query: params ?? {} },
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
	});
};

export const useCreateActivePlayers: CustomMutationHook<
	postAdminAllActivePlayersInput,
	postAdminAllActivePlayersOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		postAdminAllActivePlayersOutput,
		ApiError,
		postAdminAllActivePlayersInput
	>({
		mutationKey: ACTIVE_PLAYERS_SERVICE_KEYS.useCreateActivePlayers(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/admin/pa-transfer-players/",
				{
					body: body.resource_ids.map((resource_id) => ({
						resource_id,
						activation_state: "ACTIVE",
					})),
					throwOnError: true,
				},
			);
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: () => {
			invalidateQueries(ACTIVE_PLAYERS_SERVICE_KEYS.useGetAllActivePlayers());
			invalidateQueries(
				ACTIVE_PLAYERS_SERVICE_KEYS.useGetActivePlayersFacets(),
			);
			invalidateActivePlayerSuggestions();
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useCreateActivePlayer: CustomMutationHook<
	createActivePlayerInput,
	createActivePlayerOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		createActivePlayerOutput,
		ApiError,
		createActivePlayerInput
	>({
		mutationKey: ACTIVE_PLAYERS_SERVICE_KEYS.useCreateActivePlayer(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/pa-transfer-player/",
				{
					body,
					throwOnError: true,
				},
			);
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: () => {
			invalidateQueries(ACTIVE_PLAYERS_SERVICE_KEYS.useGetAllActivePlayers());
			invalidateQueries(
				ACTIVE_PLAYERS_SERVICE_KEYS.useGetActivePlayersFacets(),
			);
			invalidateActivePlayerSuggestions();
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useGetActivePlayer: CustomQueryHook<
	getActivePlayerOutput,
	getActivePlayerInput
> = (params, options) => {
	return useQuery({
		...options,
		queryKey: ACTIVE_PLAYERS_SERVICE_KEYS.useGetActivePlayer(params),
		enabled: !!params.active_player_id,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/pa-transfer-player/{pa_transfer_player_id}",
				{
					params: {
						path: { pa_transfer_player_id: params.active_player_id },
					},
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
	});
};

export const useUpdateActivePlayer: CustomMutationHook<
	updateActivePlayerInput,
	updateActivePlayerOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		updateActivePlayerOutput,
		ApiError,
		updateActivePlayerInput
	>({
		mutationKey: ACTIVE_PLAYERS_SERVICE_KEYS.useUpdateActivePlayer(),
		mutationFn: async (payload) => {
			const { active_player_id, ...body } = payload;

			const { data, error } = await fetchClient.PATCH(
				"/v1/fc/pa-transfer-player/{pa_transfer_player_id}",
				{
					params: {
						path: { pa_transfer_player_id: active_player_id },
					},
					body,
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			invalidateQueries(ACTIVE_PLAYERS_SERVICE_KEYS.useGetAllActivePlayers());
			invalidateQueries(
				ACTIVE_PLAYERS_SERVICE_KEYS.useGetActivePlayersFacets(),
			);
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useDeleteActivePlayer: CustomMutationHook<
	deleteActivePlayerInput,
	deleteActivePlayerOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		deleteActivePlayerOutput,
		ApiError,
		deleteActivePlayerInput
	>({
		mutationKey: ACTIVE_PLAYERS_SERVICE_KEYS.useDeleteActivePlayer(),
		mutationFn: async (payload) => {
			const { active_player_id } = payload;

			const { data, error } = await fetchClient.DELETE(
				"/v1/fc/pa-transfer-player/{pa_transfer_player_id}",
				{
					params: {
						path: { pa_transfer_player_id: active_player_id },
					},
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			invalidateQueries(ACTIVE_PLAYERS_SERVICE_KEYS.useGetAllActivePlayers());
			invalidateQueries(
				ACTIVE_PLAYERS_SERVICE_KEYS.useGetActivePlayersFacets(),
			);
			invalidateActivePlayerSuggestions();
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useBatchDeleteTransferPlayers: CustomMutationHook<
	BatchDeleteTransferPlayersInput,
	BatchDeleteTransferPlayersOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: ACTIVE_PLAYERS_SERVICE_KEYS.useBatchDeleteTransferPlayers(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/admin/pa-transfer-players/delete-many/",
				{
					body,
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			invalidateQueries(ACTIVE_PLAYERS_SERVICE_KEYS.useGetAllActivePlayers());
			invalidateQueries(
				ACTIVE_PLAYERS_SERVICE_KEYS.useGetActivePlayersFacets(),
			);
			invalidateActivePlayerSuggestions();
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

type BatchUpdateTransferPlayersInput = {
	ids: string[];
	activation_state: Extract<
		TransferPlayerState,
		"ACTIVE" | "DEACTIVATED_MANUALLY"
	>;
};

export const useBatchUpdateTransferPlayers = () => {
	const { toastApiError } = useApiError();

	return useMutation<void, ApiError, BatchUpdateTransferPlayersInput>({
		mutationKey: ACTIVE_PLAYERS_SERVICE_KEYS.useBatchUpdateTransferPlayers(),
		mutationFn: async (body) => {
			const { error } = await fetchClient.POST(
				"/v1/fc/admin/pa-transfer-players/update-many/",
				{ body, throwOnError: true },
			);
			if (error) throw error;
		},
		onSuccess: () => {
			invalidateQueries(ACTIVE_PLAYERS_SERVICE_KEYS.useGetAllActivePlayers());
			invalidateQueries(
				ACTIVE_PLAYERS_SERVICE_KEYS.useGetActivePlayersFacets(),
			);
		},
		onError: toastApiError,
	});
};

export const ACTIVE_PLAYERS_FACET_SOURCE =
	createFacetSource<getActivePlayersFacetsOutput>({
		path: "/v1/fc/pa-transfer-player/facets/",
		queryKey: (query) =>
			ACTIVE_PLAYERS_SERVICE_KEYS.useGetActivePlayersFacets(query as any),
	});

export const ACTIVE_PLAYER_SUGGESTIONS_FACET_SOURCE =
	createFacetSource<getActivePlayerSuggestionsFacetsOutput>({
		path: "/v1/fc/pa-transfer-player-suggestions/facets/",
		queryKey: (query) =>
			ACTIVE_PLAYERS_SERVICE_KEYS.useGetActivePlayerSuggestionsFacets(
				query as any,
			),
	});

export const useGetPlayerDatabase: CustomQueryHook<
	getPlayerDatabaseOutput,
	getPlayerDatabaseInput
> = (params, options) => {
	return useQuery<getPlayerDatabaseOutput, ApiError>({
		...options,
		queryKey: ACTIVE_PLAYERS_SERVICE_KEYS.useGetPlayerDatabase(params ?? {}),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/admin/player-database/",
				{ params: { query: params ?? {} }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
	});
};

export const PLAYER_DATABASE_FACET_SOURCE =
	createFacetSource<getPlayerDatabaseFacetsOutput>({
		path: "/v1/fc/admin/player-database/facets/",
		queryKey: (query) =>
			ACTIVE_PLAYERS_SERVICE_KEYS.useGetPlayerDatabaseFacets(query as any),
	});

/** Fetches price and price range from fut.gg right now; every player list shows the new ones. */
export const useRefreshPlayerPrices: CustomMutationHook<
	refreshPlayerPricesInput,
	refreshPlayerPricesOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation<
		refreshPlayerPricesOutput,
		ApiError,
		refreshPlayerPricesInput
	>({
		mutationKey: ACTIVE_PLAYERS_SERVICE_KEYS.useRefreshPlayerPrices(),
		mutationFn: async (body) => {
			// the backend takes up to 100 ids per call
			const ids = [...new Set(body.resource_ids)];
			const refreshed: refreshPlayerPricesOutput = [];
			for (let i = 0; i < ids.length; i += 100) {
				const { data, error } = await fetchClient.POST(
					"/v1/fc/admin/players/refresh-prices/",
					{
						body: { resource_ids: ids.slice(i, i + 100) },
						throwOnError: true,
					},
				);
				if (error) throw error;
				refreshed.push(...data);
			}
			return refreshed;
		},
		onSuccess: (data, body) => {
			const requested = new Set(body.resource_ids).size;
			if (data.length === requested) {
				toast.success(
					t("adminTransferPlayers.refreshPrice.success", {
						count: data.length,
					}),
				);
			} else {
				toast.warning(
					t("adminTransferPlayers.refreshPrice.partial", {
						count: data.length,
						total: requested,
					}),
				);
			}
		},
		// a later batch can fail after earlier ones saved prices, so refresh either way
		onSettled: () => {
			invalidateQueries(ACTIVE_PLAYERS_SERVICE_KEYS.useGetAllActivePlayers());
			invalidateQueries(
				ACTIVE_PLAYERS_SERVICE_KEYS.useGetActivePlayerSuggestions(),
			);
			invalidateQueries(ACTIVE_PLAYERS_SERVICE_KEYS.useGetPlayerDatabase());
		},
		onError: toastApiError,
	});
};
